
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PageHeader, Card, Table, Thead, Tbody, Tr, Th, Td } from '../components/UI';
import { Search, Download, Check, AlertCircle, FileText, ChevronRight, ChevronLeft } from '../components/Icons';
import { DocumentPreview } from '../components/DocumentPreview';
import { useTranslation } from '../contexts/LanguageContext';

// Mock Data specific to Supplier View
const mockOrders = [
  { id: 'PO-9001', date: '2025-10-24', item: 'Industrial Ball Bearing X200', qty: 150, status: 'OPEN', amount: '1,200.00' },
  { id: 'PO-9002', date: '2025-10-23', item: 'Conveyor Belt segment', qty: 10, status: 'SHIPPED', amount: '4,500.00' },
  { id: 'PO-8995', date: '2025-10-20', item: 'Hydraulic Seal 50mm', qty: 200, status: 'DELIVERED', amount: '850.00' },
  { id: 'PO-8990', date: '2025-10-18', item: 'Control Module V4', qty: 5, status: 'OPEN', amount: '2,100.00' },
  { id: 'PO-8988', date: '2025-10-15', item: 'Pressure Sensor 10bar', qty: 25, status: 'DELIVERED', amount: '625.00' },
];

const SupplierOrders: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [viewingOrder, setViewingOrder] = useState<any | null>(null);

  const tabs = ['All', 'Open', 'Shipped', 'Delivered'];

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
  };

  const filteredOrders = mockOrders.filter(o => {
    // Tab Filter
    if (activeTab !== 'All' && o.status !== activeTab.toUpperCase()) return false;

    // Search Filter
    if (search) {
      const s = search.toLowerCase();
      const matchesSearch = o.id.toLowerCase().includes(s) || o.item.toLowerCase().includes(s);
      if (!matchesSearch) return false;
    }

    // Column Filters
    return Object.entries(columnFilters).every(([key, value]) => {
      if (!value) return true;
      const itemValue = String((o as any)[key] || '').toLowerCase();
      return itemValue.includes((value as string).toLowerCase());
    });
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'DELIVERED':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-900/30">
            <Check size={10} strokeWidth={4} /> {t('delivered')}
          </span>
        );
      case 'OPEN':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-900/30">
            <AlertCircle size={10} strokeWidth={4} /> {t('open')}
          </span>
        );
      case 'SHIPPED':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-900/30">
            <Check size={10} strokeWidth={4} /> {t('shipped')}
          </span>
        );
      default:
        return null;
    }
  };

  if (viewingOrder) {
    const doc = {
      orderNumber: viewingOrder.id,
      date: viewingOrder.date,
      customer: 'VMI Hub Enterprise',
      title: viewingOrder.item,
      net: parseFloat(viewingOrder.amount.replace(/,/g, '')),
      gross: parseFloat(viewingOrder.amount.replace(/,/g, '')) * 1.081,
      status: viewingOrder.status === 'DELIVERED' ? 'completed' : 'sendable'
    };
    return <DocumentPreview type="ORDER" document={doc} onBack={() => setViewingOrder(null)} />;
  }

  return (
    <div className="p-6 space-y-6 max-w-[1920px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title={t('orders')} 
        breadcrumbs={[{ label: user?.companyName || 'Supplier' }, { label: t('orders') }]}
      />

      <Card className="overflow-hidden border-t-4 border-t-brand-medium">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-[#393939] bg-white dark:bg-[#161616] flex flex-wrap items-center justify-between gap-4">
           {/* Tab Switch */}
           <div className="flex items-center gap-1 bg-[#F5F7FA] dark:bg-[#262626] p-1 rounded-md">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`h-8 px-4 text-[10px] font-bold uppercase tracking-widest transition-all rounded-sm ${
                  activeTab === tab
                    ? 'bg-white dark:bg-[#161616] text-brand-dark dark:text-white shadow-sm'
                    : 'text-gray-500 hover:text-brand-dark dark:hover:text-white'
                }`}
              >
                {t(tab.toLowerCase() as any)}
              </button>
            ))}
          </div>
           
           <div className="flex items-center gap-3">
              <div className="relative group">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-medium transition-colors" />
                <input 
                  type="text" 
                  placeholder={t('search_orders')} 
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-9 pl-9 pr-4 bg-[#F5F7FA] dark:bg-[#262626] border border-transparent focus:border-brand-medium focus:bg-white dark:focus:bg-[#1F1F1F] rounded-sm text-xs w-64 outline-none transition-all"
                />
              </div>
              <button 
               className="h-9 px-4 flex items-center gap-2 bg-white dark:bg-[#262626] border border-gray-200 dark:border-[#393939] hover:bg-gray-50 dark:hover:bg-[#333] text-gray-600 dark:text-gray-300 text-[10px] font-bold uppercase tracking-widest rounded-sm transition-all"
             >
                <Download size={14} /> {t('export_list')}
             </button>
           </div>
        </div>

        <Table>
          <Thead>
            <Tr isHeader>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('po_number')}</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('id', e.target.value)}
                  />
                </div>
              </Th>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('order_date')}</span>
                  <input 
                    type="date"
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    onChange={(e) => handleColumnFilterChange('date', e.target.value)}
                  />
                </div>
              </Th>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('item_name')}</span>
                  <input 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('item', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="text-right">
                <div className="flex flex-col gap-2 items-end">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('order_qty')}</span>
                  <input 
                    className="w-20 bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400 text-right"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('qty', e.target.value)}
                  />
                </div>
              </Th>
              <Th className="text-right">
                <div className="flex flex-col gap-2 items-end">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('amount')} (CHF)</span>
                  <input 
                    className="w-24 bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400 text-right"
                    placeholder={t('filter_placeholder')}
                    onChange={(e) => handleColumnFilterChange('amount', e.target.value)}
                  />
                </div>
              </Th>
              <Th>
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('status')}</span>
                  <select 
                    className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-1 focus:outline-none focus:border-brand-medium transition-colors cursor-pointer text-gray-600 dark:text-gray-300"
                    onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                  >
                    <option value="">{t('all')}</option>
                    <option value="OPEN">{t('open')}</option>
                    <option value="SHIPPED">{t('shipped')}</option>
                    <option value="DELIVERED">{t('delivered')}</option>
                  </select>
                </div>
              </Th>
              <Th className="text-right">
                 {t('actions')}
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredOrders.length > 0 ? filteredOrders.map((order) => (
              <Tr key={order.id} onClick={() => setViewingOrder(order)}>
                <Td>
                  <span className="font-bold text-brand-dark dark:text-white font-mono text-xs flex items-center gap-2">
                    <FileText size={12} className="text-gray-400" />
                    {order.id}
                  </span>
                </Td>
                <Td className="text-sm text-gray-600 dark:text-gray-400">
                  {order.date}
                </Td>
                <Td className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {order.item}
                </Td>
                <Td className="text-right font-medium">
                  {order.qty}
                </Td>
                <Td className="text-right font-medium text-brand-dark dark:text-white">
                  {order.amount}
                </Td>
                <Td>
                  {getStatusBadge(order.status)}
                </Td>
                <Td className="text-right">
                  <button className="text-gray-400 hover:text-brand-dark dark:hover:text-white transition-colors" onClick={() => setViewingOrder(order)}>
                     <ChevronRight size={16} />
                  </button>
                </Td>
              </Tr>
            )) : (
              <Tr>
                <Td colSpan={7} className="text-center py-12 text-xs text-gray-400 uppercase tracking-widest font-bold">
                  No orders found.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-[#393939] flex items-center justify-between gap-2 bg-gray-50/50 dark:bg-[#161616]">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            {t('showing_x_of_y').replace('{x}', String(filteredOrders.length)).replace('{y}', String(filteredOrders.length))}
          </div>
          <div className="flex items-center gap-1">
            <button className="p-1.5 text-gray-400 hover:text-brand-dark dark:hover:text-white transition-colors"><ChevronLeft size={16} /></button>
            <button className="p-1.5 text-gray-400 hover:text-brand-dark dark:hover:text-white transition-colors"><ChevronRight size={16} /></button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SupplierOrders;
